// Rattacher un mail DÉPOSÉ (glisser-déposer) à l'élément de la boîte dont il
// provient.
//
// LE PROBLÈME. Outlook livre un mail déposé comme un blob .eml sans aucun
// identifiant (cf. dragDrop.ts) — or poser la catégorie « Rangé » exige un
// `itemId`. La sélection Outlook, elle, expose les deux : `itemId` ET
// `internetMessageId` (SelectedItemDetails, Mailbox 1.14, déjà disponible
// puisque le manifeste exige 1.15). Le Message-ID lu dans les en-têtes du blob
// fait donc le pont entre le mail déposé et son élément d'origine.
//
// POURQUOI VÉRIFIER LE MESSAGE-ID PLUTÔT QUE DE CROIRE LA SÉLECTION. Faire
// glisser un mail le sélectionne, donc « le mail déposé = le mail sélectionné »
// est vrai la plupart du temps. Mais pas toujours : un .msg ou un .eml déposé
// depuis l'explorateur de fichiers ne provient d'AUCUN élément de la boîte, et
// la sélection Outlook désigne alors un mail sans rapport. L'étiqueter serait
// une affirmation fausse — « ce mail est rangé » sur un mail que personne n'a
// rangé — et l'utilisateur n'aurait aucun moyen de comprendre d'où sort la
// pastille verte. Un Message-ID est unique par construction : l'exiger rend la
// confusion impossible, au prix d'un étiquetage abandonné quand on ne peut pas
// conclure.
import type { SelectedItemInfo } from './selection';

/**
 * Forme comparable d'un Message-ID RFC 5322.
 *
 * INDISPENSABLE : les deux bouts de la comparaison ne l'écrivent PAS pareil.
 * Office rend `<abc@example.com>`, chevrons compris ; l'en-tête du .eml peut
 * arriver avec une casse quelconque et des espaces autour. Sans normalisation la
 * comparaison échoue toujours, et la fonctionnalité paraît morte sans rien
 * signaler.
 */
export function normalizeMessageId(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const trimmed = raw.trim().replace(/^</, '').replace(/>$/, '').trim();
  // Comparaison en minuscules : la partie domaine n'est pas sensible à la casse,
  // et une divergence de casse ne doit pas coûter une pastille verte.
  return trimmed ? trimmed.toLowerCase() : null;
}

// 64 Ko décodés, comme la fonction edge : un fil de discussion long pousse le
// bloc d'en-têtes bien au-delà de quelques Ko. 4 caractères de base64 valant
// 3 octets, 65 536 octets tiennent dans 87 384 caractères (multiple de 4).
const HEADER_B64_CHARS = 87_384;

/**
 * Message-ID lu dans les en-têtes d'un mail .eml encodé en base64, normalisé.
 * `null` si on ne peut pas le lire — on renonce alors à étiqueter, jamais à
 * ranger.
 */
export function messageIdFromEmlBase64(contentBase64: string): string | null {
  try {
    // Découpe AVANT de nettoyer les espaces : le blob peut peser plusieurs Mo, et
    // seul le début nous intéresse. `atob` refuse un reste incomplet, d'où
    // l'alignement sur un multiple de 4.
    const head = contentBase64.slice(0, HEADER_B64_CHARS).replace(/\s+/g, '');
    const aligned = head.slice(0, head.length - (head.length % 4));
    if (!aligned) return null;
    const text = atob(aligned);
    // Le bloc d'en-têtes s'arrête à la première ligne vide. Sans cette coupe, un
    // « Message-ID: » cité dans le corps — ou dans un mail joint en pièce
    // jointe, ce qui est courant ici — pourrait passer pour celui du mail.
    const blank = text.search(/\r?\n\r?\n/);
    const headers = blank >= 0 ? text.slice(0, blank) : text;
    // Même expression que `extractMessageIdRfc` côté serveur
    // (functions/_outlook_shared/hash.ts) : les deux doivent lire LA MÊME valeur,
    // sous peine de ranger sous une clé et d'étiqueter d'après une autre.
    const m = headers.match(/^Message-ID:\s*<?([^>\r\n]+)>?\s*$/im);
    return normalizeMessageId(m?.[1]);
  } catch {
    return null;
  }
}

/**
 * Index Message-ID normalisé → `itemId` EWS, construit sur la sélection Outlook.
 *
 * Les éléments sans `internetMessageId` sont ignorés plutôt que rangés sous une
 * clé vide : sinon deux mails inconnus se recouvriraient et le mauvais serait
 * étiqueté.
 */
export function indexSelectionByMessageId(items: SelectedItemInfo[]): Map<string, string> {
  const index = new Map<string, string>();
  for (const item of items) {
    const key = normalizeMessageId(item.internetMessageId);
    if (key && item.itemId) index.set(key, item.itemId);
  }
  return index;
}
